import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import ServicesSection, { SERVICES } from "../components/ServicesSection";
import LeadForm from "../components/LeadForm";
import useDocumentMeta from "../lib/useDocumentMeta";
import { CONSULT_LINK } from "../lib/constants";

const processSteps = [
    {
        n: "01",
        t: "Discover",
        d: "We start with a deep brief — your business, audience, and goals. No assumptions.",
    },
    {
        n: "02",
        t: "Design",
        d: "Concepts grounded in strategy. You'll see real options, not infinite revisions of the same idea.",
    },
    {
        n: "03",
        t: "Refine",
        d: "We tighten the chosen direction with focused feedback rounds and craft passes.",
    },
    {
        n: "04",
        t: "Deliver",
        d: "Production‑ready files, brand guidelines, and on‑location signage installation when needed.",
    },
];

const includes = [
    "Logo design with multiple concepts",
    "Brand guideline document",
    "Print & digital ready files",
    "Free 1‑month support after delivery",
    "Source files (AI, PSD, Figma)",
    "Founder‑level project ownership",
];

const ServicesPage = () => {
    useDocumentMeta({
        title: "Graphic Design Services in Nagpur — Logo, Branding, Printing | Raj Graphics",
        description:
            "Logo design, brand identity, packaging, signage, printing, social media and website design in Nagpur — a full-service design studio for growing businesses.",
        canonical: "/services",
    });

    return (
        <main data-testid="services-page" className="bg-[#050505]">
            {/* Hero */}
            <section className="relative pt-36 pb-16 md:pt-44 md:pb-24 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 -left-20 w-[500px] h-[500px] rounded-full bg-gold/[0.07] blur-[120px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Services
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl"
                        data-testid="services-page-headline"
                    >
                        Everything your brand needs — <span className="gold-gradient-text">in one studio</span>.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        From the first concept sketch to the storefront install, we handle every
                        visual touchpoint of your brand.
                    </motion.p>
                </div>
            </section>

            {/* Services grid */}
            <ServicesSection showHeading={false} />

            {/* What's included */}
            <section className="relative py-24 md:py-32 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                            What you get
                        </div>
                        <h2 className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
                            More than a deliverable — a system you can grow with.
                        </h2>
                    </div>
                    <div className="lg:col-span-7">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {includes.map((it) => (
                                <li
                                    key={it}
                                    data-testid={`included-${it.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
                                    className="flex items-start gap-3 bg-white/[0.02] border border-white/5 rounded-xl p-5 hover:border-gold/30 transition-colors"
                                >
                                    <span className="w-6 h-6 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check size={12} className="text-gold" strokeWidth={2.5} />
                                    </span>
                                    <span className="font-inter text-sm text-zinc-300">{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="relative py-24 md:py-32 border-t border-white/5 bg-[#080808]">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="max-w-2xl mb-14">
                        <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                            Our Process
                        </div>
                        <h2 className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
                            A clear, predictable path from brief to brand.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                        {processSteps.map((st, i) => (
                            <motion.div
                                key={st.n}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.5, delay: i * 0.07 }}
                                data-testid={`process-step-${st.n}`}
                                className="bg-[#0c0c0c] border border-white/5 rounded-2xl p-7 hover:border-gold/30 transition-colors"
                            >
                                <div className="font-poppins font-semibold text-gold text-3xl">{st.n}</div>
                                <h3 className="mt-5 font-poppins font-medium text-white text-lg">{st.t}</h3>
                                <p className="mt-2 font-inter text-sm text-zinc-400 leading-relaxed">{st.d}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service tags */}
            <section className="relative py-16 md:py-20 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">
                        Things we make
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {SERVICES.map((s) => (
                            <span
                                key={s.id}
                                className="inline-flex items-center font-inter text-sm text-zinc-300 border border-white/10 rounded-full px-4 py-2 hover:border-gold hover:text-gold transition-colors"
                            >
                                {s.title}
                            </span>
                        ))}
                    </div>
                    <a
                        href={CONSULT_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="services-bottom-cta"
                        className="mt-12 inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] text-black font-medium font-inter px-7 py-3.5 rounded-full transition-colors"
                    >
                        Book Free Consultation <ArrowRight size={18} />
                    </a>
                </div>
            </section>

            <LeadForm />
        </main>
    );
};

export default ServicesPage;
